/**
 * frontend/src/components/BillingTab.jsx
 *
 * Drop into CompanyAdminDashboard.jsx as the "Billing" tab.
 *
 * Usage:
 *   import BillingTab from '../components/BillingTab';
 *
 *   // 1. Add to your tabs array:
 *   { id: 'billing', label: 'Billing', icon: <CreditCard className="w-4 h-4" /> }
 *
 *   // 2. Add to your tab content:
 *   {activeTab === 'billing' && <BillingTab />}
 */

import React, { useEffect, useState } from 'react';
import {
  CreditCard, FileText, ExternalLink, AlertTriangle,
  CheckCircle, Clock, XCircle, Loader2, ArrowRight,
  RefreshCw, Download, Shield
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';

// ─── constants ───────────────────────────────────────────────────────────────

const STATUS_CONFIG = {
  active:   { icon: CheckCircle,    color: 'text-green-400',  bg: 'bg-green-500/10  border-green-500/30',  label: 'Active' },
  trialing: { icon: Clock,          color: 'text-blue-400',   bg: 'bg-blue-500/10   border-blue-500/30',   label: 'Trial' },
  past_due: { icon: AlertTriangle,  color: 'text-yellow-400', bg: 'bg-yellow-500/10 border-yellow-500/30', label: 'Past Due' },
  canceled: { icon: XCircle,        color: 'text-red-400',    bg: 'bg-red-500/10    border-red-500/30',    label: 'Canceled' },
  inactive: { icon: CreditCard,     color: 'text-slate-400',  bg: 'bg-slate-800     border-slate-700',     label: 'No Subscription' },
};

// ─── sub-components ──────────────────────────────────────────────────────────

function SectionCard({ title, icon: Icon, children }) {
  return (
    <div className="bg-slate-800/60 border border-slate-700 rounded-xl overflow-hidden">
      <div className="flex items-center gap-2 px-5 py-4 border-b border-slate-700">
        <Icon className="w-4 h-4 text-slate-400" />
        <h3 className="text-sm font-semibold text-slate-300 uppercase tracking-wider">{title}</h3>
      </div>
      <div className="p-5">{children}</div>
    </div>
  );
}

function SpecBadge({ label }) {
  return (
    <span className="inline-flex items-center px-2.5 py-1 rounded-full bg-slate-700/60 border border-slate-600 text-xs text-slate-300">
      {label}
    </span>
  );
}

// ─── main ────────────────────────────────────────────────────────────────────

export default function BillingTab() {
  const navigate = useNavigate();

  const [subscription, setSubscription] = useState(undefined); // undefined = loading
  const [invoices,     setInvoices]     = useState([]);
  const [loadingInv,   setLoadingInv]   = useState(true);
  const [managing,     setManaging]     = useState(false);
  const [error,        setError]        = useState('');

  useEffect(() => { fetchAll(); }, []);

  async function fetchAll() {
    try {
      const [subRes, invRes] = await Promise.allSettled([
        api.get('/stripe/my-subscription'),
        api.get('/stripe/my-invoices'),
      ]);

      if (subRes.status === 'fulfilled') {
        setSubscription(subRes.value.data.subscription ?? null);
      } else {
        setSubscription(null);
      }

      if (invRes.status === 'fulfilled') {
        setInvoices(invRes.value.data);
      }
    } catch (err) {
      setError('Failed to load billing information.');
    } finally {
      setLoadingInv(false);
    }
  }

  async function openPortal() {
    setManaging(true);
    setError('');
    try {
      const { data } = await api.post('/stripe/create-portal-session');
      window.location.href = data.url;
    } catch (err) {
      setError(err.response?.data?.error ?? 'Failed to open billing portal.');
      setManaging(false);
    }
  }

  // Still loading subscription
  if (subscription === undefined) {
    return (
      <div className="flex justify-center py-16">
        <Loader2 className="w-7 h-7 animate-spin text-blue-400" />
      </div>
    );
  }

  const stripeStatus = subscription?.stripe_status ?? 'inactive';
  const cfg          = STATUS_CONFIG[stripeStatus] ?? STATUS_CONFIG.inactive;
  const StatusIcon   = cfg.icon;
  const isActive     = ['active', 'trialing'].includes(stripeStatus);

  const periodEnd = subscription?.current_period_end
    ? new Date(subscription.current_period_end).toLocaleDateString('en-US', {
        month: 'long', day: 'numeric', year: 'numeric',
      })
    : null;

  return (
    <div className="space-y-5 max-w-3xl">

      {/* ── Error banner ─────────────────────────────────────────────────── */}
      {error && (
        <div className="flex items-center gap-2 bg-red-500/10 border border-red-500/30 text-red-400 rounded-lg px-4 py-3 text-sm">
          <AlertTriangle className="w-4 h-4 flex-shrink-0" />
          {error}
        </div>
      )}

      {/* ── Current subscription ─────────────────────────────────────────── */}
      <SectionCard title="Subscription" icon={Shield}>
        {/* Status pill */}
        <div className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-lg border text-sm font-medium mb-5 ${cfg.bg} ${cfg.color}`}>
          <StatusIcon className="w-4 h-4" />
          {cfg.label}
          {subscription?.cancel_at_period_end && (
            <span className="text-yellow-400 font-normal">— cancels {periodEnd}</span>
          )}
        </div>

        {isActive && subscription ? (
          <>
            {/* Plan details grid */}
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 mb-5">
              <div>
                <p className="text-xs text-slate-400 mb-1">Plan</p>
                <p className="font-semibold text-white">{subscription.plan_name}</p>
              </div>
              <div>
                <p className="text-xs text-slate-400 mb-1">Monthly Cost</p>
                <p className="font-semibold text-white">
                  ${parseFloat(subscription.price_monthly || subscription.price_total).toFixed(2)}
                </p>
              </div>
              <div>
                <p className="text-xs text-slate-400 mb-1">
                  {subscription.cancel_at_period_end ? 'Cancels On' : 'Renews On'}
                </p>
                <p className="font-semibold text-white">{periodEnd ?? '—'}</p>
              </div>
            </div>

            {/* Spec badges */}
            {(subscription.storage_gb || subscription.ram_gb || subscription.vcpu) && (
              <div className="flex flex-wrap gap-2 mb-5">
                {subscription.storage_gb && <SpecBadge label={`${subscription.storage_gb}GB Storage`} />}
                {subscription.ram_gb     && <SpecBadge label={`${subscription.ram_gb}GB RAM`} />}
                {subscription.vcpu       && <SpecBadge label={`${subscription.vcpu} vCPU`} />}
              </div>
            )}

            {/* Cancel warning */}
            {subscription.cancel_at_period_end && (
              <div className="flex items-start gap-2 bg-yellow-500/10 border border-yellow-500/30 text-yellow-400 rounded-lg px-3 py-2.5 text-sm mb-5">
                <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                <span>
                  Your subscription will cancel on <strong>{periodEnd}</strong>.
                  You can reactivate it from the billing portal before that date.
                </span>
              </div>
            )}

            {/* Manage button */}
            <button
              onClick={openPortal}
              disabled={managing}
              className="inline-flex items-center gap-2 bg-blue-500/10 hover:bg-blue-500/20 border border-blue-500/30 text-blue-400 px-4 py-2 rounded-lg text-sm font-medium transition-colors"
            >
              {managing
                ? <Loader2 className="w-4 h-4 animate-spin" />
                : <CreditCard className="w-4 h-4" />}
              Manage Billing & Payment
              <ExternalLink className="w-3 h-3" />
            </button>
          </>
        ) : stripeStatus === 'past_due' ? (
          <div className="space-y-4">
            <p className="text-slate-400 text-sm">
              Your last payment failed. Update your payment method to keep your subscription active.
            </p>
            <button
              onClick={openPortal}
              disabled={managing}
              className="inline-flex items-center gap-2 bg-yellow-500/10 hover:bg-yellow-500/20 border border-yellow-500/30 text-yellow-400 px-4 py-2 rounded-lg text-sm font-medium transition-colors"
            >
              {managing ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
              Update Payment Method
            </button>
          </div>
        ) : (
          <div className="space-y-3">
            <p className="text-slate-400 text-sm">
              Your company doesn't have an active subscription yet.
            </p>
            <button
              onClick={() => navigate('/pricing')}
              className="inline-flex items-center gap-2 bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded-lg text-sm font-semibold transition-colors"
            >
              View Plans <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        )}
      </SectionCard>

      {/* ── Invoice history ───────────────────────────────────────────────── */}
      <SectionCard title="Invoice History" icon={FileText}>
        {loadingInv ? (
          <div className="flex justify-center py-6">
            <Loader2 className="w-5 h-5 animate-spin text-blue-400" />
          </div>
        ) : invoices.length === 0 ? (
          <p className="text-slate-500 text-sm text-center py-6">
            No invoices yet. They'll appear here after your first payment.
          </p>
        ) : (
          <div className="space-y-2">
            {invoices.map(inv => {
              const isPaid = inv.status === 'paid';
              return (
                <div
                  key={inv.stripe_invoice_id}
                  className="flex items-center justify-between py-3 px-4 rounded-lg bg-slate-900/50 border border-slate-700/50 hover:border-slate-600 transition-colors"
                >
                  <div className="flex items-center gap-3">
                    <span className={`text-xs font-semibold px-2 py-0.5 rounded-full border
                      ${isPaid
                        ? 'bg-green-500/10 text-green-400 border-green-500/30'
                        : 'bg-red-500/10  text-red-400  border-red-500/30'}`}>
                      {inv.status}
                    </span>
                    <div>
                      <p className="text-sm font-medium text-white">
                        ${(inv.amount_paid / 100).toFixed(2)}{' '}
                        <span className="text-slate-400 text-xs uppercase">{inv.currency}</span>
                      </p>
                      {inv.period_start && inv.period_end && (
                        <p className="text-xs text-slate-500">
                          {new Date(inv.period_start).toLocaleDateString()} –{' '}
                          {new Date(inv.period_end).toLocaleDateString()}
                        </p>
                      )}
                    </div>
                  </div>

                  <div className="flex items-center gap-2">
                    {inv.invoice_pdf && (
                      <a
                        href={inv.invoice_pdf}
                        target="_blank"
                        rel="noopener noreferrer"
                        title="Download PDF"
                        className="p-1.5 rounded-lg text-slate-400 hover:text-blue-400 hover:bg-blue-500/10 transition-colors"
                      >
                        <Download className="w-4 h-4" />
                      </a>
                    )}
                    {inv.hosted_invoice_url && (
                      <a
                        href={inv.hosted_invoice_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        title="View invoice"
                        className="p-1.5 rounded-lg text-slate-400 hover:text-blue-400 hover:bg-blue-500/10 transition-colors"
                      >
                        <ExternalLink className="w-4 h-4" />
                      </a>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </SectionCard>

    </div>
  );
}
